import BaseListPresenter from "../../../base/BaseListPresenter";
import { getCurrentUserUseCase } from "../../../usecases/user";
import xhr from "../../../usecases/xhr/Xhr";

class SalesDiscountAmountPresenter extends BaseListPresenter {
  init() {
    this.store = [];
    // this.limit = 2000000;
    this.where = {};
    this.search = {};
    this.filter = {};
    this.filterDate = {};
    this.filterStore = {};
    this.include = ["all"];
    this.keys = undefined; // if keys are specified, only those keys will be returned
    this.sort = { createdAt: -1 };
    this.progress = true;
    this.reset();
  }

  componentDidMount() {
    this.init();
    this.findStore();
    return this.getObjects();
  }

  componentWillUnmount() {
    this.cleanup();
  }

  async getObjects() {
    this.reset();
    this.find();
  }

  async findStore() {
    const user = await getCurrentUserUseCase().execute();
    const storeQuery = { include: ["all"], where: { email: user.email } };
    this.findObjectUseCase.abort();
    this.store = await this.findObjectUseCase.execute("users", storeQuery);

    const store = (this.store?.[0]?.storeAnalytic ?? []).sort((a, b) =>
      a.name.toUpperCase().localeCompare(b.name.toUpperCase()),
    );
    this.view.setStore(store);
  }

  async find() {
    try {
      if (this.abortController && !this.abortController.signal.aborted) {
        this.abortController.abort();
        console.log("Aborted previous requests");
        await new Promise((resolve) => setTimeout(resolve, 100));
      }

      this.abortController = new AbortController();
      this.objects = [];
      const startTime = Date.now();

      const query = this.createQuery({ skip: 0, limit: this.limit });
      delete query.limit;
      delete query.include;

      const startDate = query.where?.tran_date?.$gte || "";
      const endDate = query.where?.tran_date?.$lte || "";

      console.log("startDate", startDate, "endDate", endDate);

      const dateRanges = this.generateDateRanges(startDate, endDate);

      for (let i = 0; i < dateRanges.length; i++) {
        const range = dateRanges[i];

        const chunkQuery = {
          ...query,
          where: {
            ...query.where,
            tran_date: {
              $gte: range.start,
              $lte: range.end,
            },
          },
        };

        console.log("chunkQuery", chunkQuery);

        try {
          const chunkData = await xhr.get("/transactions", {
            params: chunkQuery,
            signal: this.abortController.signal,
          });

          this.objects = this.objects.concat(chunkData);
          this.view.setObjects(this.objects);
        } catch (chunkError) {
          if (chunkError.name === "AbortError") {
            console.log("Request was aborted at chunk", i + 1);
            return;
          }
          console.error(`Error fetching ${range.label}:`, chunkError.message);
        }
      }

      const downloadTime = ((Date.now() - startTime) / 1000).toFixed(1);
      console.log(
        `✅ Complete! ${this.objects.length.toLocaleString()} records in ${downloadTime}s`,
      );
      this.abortController = null;
    } catch (error) {
      if (error.name === "AbortError") {
        console.log("Entire operation was aborted");
        return;
      }
      console.error("Error:", error.message);
    }
  }

  cleanup() {
    if (this.abortController) {
      console.log("🧹 Cleaning up...");
      this.abortController.abort();
      this.abortController = null;
    }
    this.objects = [];
  }

  // Simple range generator: ≤31 days = 1 request, >31 days = monthly chunks
  generateDateRanges(startDateStr, endDateStr) {
    const start = new Date(startDateStr.split(" ")[0]);
    const end = new Date(endDateStr.split(" ")[0]);

    const daysDiff = Math.ceil((end - start) / (1000 * 60 * 60 * 24));

    console.log(`📆 Date range: ${daysDiff} days`);

    // If 31 days or less, single request
    if (daysDiff <= 31) {
      console.log("Strategy: Single request (≤31 days)");
      return [
        {
          start: this.formatDate(start),
          end: this.formatEndDate(end),
          label: `${start.toLocaleDateString()} - ${end.toLocaleDateString()}`,
        },
      ];
    }

    // More than 31 days, use monthly chunks
    console.log("Strategy: Monthly chunks (>31 days)");
    return this.generateMonthlyRanges(start, end);
  }

  // Generate monthly ranges
  generateMonthlyRanges(startDate, endDate) {
    const ranges = [];
    let current = new Date(startDate);

    while (current < endDate) {
      const monthStart = new Date(current);
      const monthEnd = new Date(
        current.getFullYear(),
        current.getMonth() + 1,
        0,
        23,
        59,
        59,
      );

      if (monthEnd > endDate) {
        monthEnd.setTime(endDate.getTime());
      }

      ranges.push({
        start: this.formatDate(monthStart),
        end: this.formatEndDate(monthEnd),
        label: monthStart.toLocaleString("default", {
          month: "short",
          year: "numeric",
        }),
      });

      current.setMonth(current.getMonth() + 1);
    }

    return ranges;
  }

  // Format date for API
  formatDate(date) {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day} 05:00:00.000`;
  }

  formatEndDate(date) {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day} 04:59:59.999`;
  }

  createQuery({ skip = 0, limit = this.limit } = {}) {
    const storeCodes =
      this.store?.flatMap((s) => s.storeAnalytic?.map((a) => a?.name)) ?? [];

    const escapedSearch = this.searchAny
      ?.trim()
      ?.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

    const whereClause = {
      ...(this.where || {}),
      ...(this.filter || {}),
      ...(this.filterDate || {}),
    };

    if (this.filterStore === "AllStore") {
      if (storeCodes.length > 0) {
        whereClause.store_code = { $in: storeCodes };
      }
    } else if (this.filterStore) {
      whereClause.store_code = this.filterStore;
    }

    // 3) Build final query
    const query = {
      limit,
      skip,
      where: whereClause,
      include: this.include,
      keys: this.keys ?? [
        "store_code",
        "tran_date",
        "total",
        "total_taxfree",
        "total_tax",
        "discount_amount",
        "createdAt",
        "status",
      ],
      sort: this.sort,
    };

    return query;
  }

  async findObjects() {
    try {
      const user = await getCurrentUserUseCase().execute();
      const storeQuery = {
        include: ["all"],
        where: { email: user.email },
      };
      this.store = await this.findObjectUseCase.execute("users", storeQuery);
      const store = this.store[0].storeAnalytic.sort((a, b) => {
        const nameA = a.name.toUpperCase();
        const nameB = b.name.toUpperCase();
        if (nameA < nameB) {
          return -1;
        }
        if (nameA > nameB) {
          return 1;
        }
        return 0; // names must be equal
      });
      this.view.setStore(store);
    } catch (error) {
      console.error("Error fetching user or store data:", error);
      return;
    }

    const skip = (this.current - 1) * this.limit;
    const storeCodes = this.store?.flatMap((storeItem) =>
      storeItem.storeAnalytic?.map((analytic) => analytic?.name),
    );

    if (Object.keys(this.filterDate ?? {}).length === 0) {
      // kailangan merong date filter para hindi mag-load ng sobrang laki
      console.log("No date filter; aborting fetch for safety.");
      return;
    }

    const query = {
      // limit: this.limit,
      skip: skip,
      where: {
        store_code:
          this.filterStore === "AllStore"
            ? { $in: storeCodes }
            : this.filterStore,
        tran_date: this.filterDate && this.filterDate,
      },
      include: ["all"],
      sort: this.sort,
      keys: [
        "store_code",
        "tran_date",
        "total",
        "total_taxfree",
        "total_tax",
        "discount_amount",
        "createdAt",
        "status",
        // "items",
      ],
    };

    try {
      this.showProgress();
      this.findObjectUseCase.abort();

      const { count } = await this.countObjectUseCase.execute(
        this.view.getCollectionName(),
        { where: query.where },
      );

      this.count = count;
      console.log("this.count", this.count);
      this.view.setCount(this.count);

      let timeoutId = null;

      // Handle delayed "not found"
      if (this.count === 0) {
        timeoutId = setTimeout(() => {
          if (this.count === 0) {
            this.view.setNotFound(true);
            this.view.setObjects([]);
            this.view.setTotal(0);
          }
        }, 15000); // 15 seconds delay
      }

      // Batching logic
      let buffer = [];

      const flushBuffer = () => {
        if (buffer.length > 0) {
          this.view.setState((prevState) => ({
            objects: [...prevState.objects, ...buffer],
          }));
          this.objects = this.objects.concat(buffer);
          this.view.setTotal(this.objects.length);
          this.view.setObjects(this.objects);
          buffer = [];
        }
      };

      const onData = (objects) => {
        if (objects.length > 0 && timeoutId) {
          clearTimeout(timeoutId);
          timeoutId = null;
          this.view.setNotFound(false);
        }
        buffer.push(...objects);
      };

      const flushInterval = setInterval(flushBuffer, 2000);

      await this.findObjectUseCase.execute(
        this.view.getCollectionName(),
        query,
        {
          timeout: 100000,
          onData,
        },
      );

      clearInterval(flushInterval);
      flushBuffer();
      // const objects = await this.findObjectUseCase.execute(
      //   this.view.getCollectionName(),
      //   query
      // );
      // this.objects = this.objects.concat(objects);
      // this.view.setTotal(this.objects.length);
      // this.view.setObjects(this.objects);
    } catch (error) {
      console.error("Error fetching objects:", error);
      this.view.showError(error);
    } finally {
      this.hideProgress();
      this.progress = false;
    }
  }

  onChangeStore(where) {
    this.filterStore = where;
    this.getObjects();
  }

  // onChangeDate(where) {
  //   const pad = (num, size = 2) => String(num).padStart(size, "0");
  //   const formatUTC = (d) => {
  //     return (
  //       [
  //         d.getUTCFullYear(),
  //         pad(d.getUTCMonth() + 1),
  //         pad(d.getUTCDate()),
  //       ].join("-") +
  //       " " +
  //       [
  //         pad(d.getUTCHours()),
  //         pad(d.getUTCMinutes()),
  //         pad(d.getUTCSeconds()),
  //       ].join(":") +
  //       "." +
  //       pad(d.getUTCMilliseconds(), 3)
  //     );
  //   };

  //   if (where && typeof where === "object") {
  //     ["$gte", "$lte"].forEach((key) => {
  //       const raw = where[key];
  //       if (typeof raw === "string") {
  //         const d = new Date(raw);
  //         if (!isNaN(d.getTime())) {
  //           if (key === "$lte") {
  //             d.setUTCDate(d.getUTCDate() + 1);
  //           }

  //           where[key] = formatUTC(d);
  //         } else {
  //           console.error(`Invalid date for ${key}:`, raw);
  //           delete where[key];
  //         }
  //       }
  //     });
  //   }

  //   if (where !== "AllMonths" && where) {
  //     this.filterDate = where;
  //   } else {
  //     delete this.filterDate;
  //   }

  //   this.getObjects();
  // }

  onChangeDate(where) {
    const timeStart = "05:00:00.000Z";
    const timeEnd = "04:59:59.999Z";

    try {
      const startDate = new Date(where.$gte);
      const endDate = new Date(where.$lte);

      if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) {
        throw new Error("Invalid date value");
      }

      const formattedStartDate = new Date(
        startDate.toISOString().split("T")[0] + "T" + timeStart,
      );

      endDate.setUTCDate(endDate.getUTCDate() + 1);
      const formattedEndDate = new Date(
        endDate.toISOString().split("T")[0] + "T" + timeEnd,
      );

      const createdAts = {
        tran_date: {
          $gte: formattedStartDate.toISOString(),
          $lte: formattedEndDate.toISOString(),
        },
      };

      console.log("GOGOGOG", createdAts);

      if (where !== "AllMonths" || !where) {
        this.filterDate = createdAts;
      } else {
        //null
        delete this.filterDate;
      }

      this.getObjects();
    } catch (error) {
      console.error("Error in onChangeDate:", error.message);
    }
  }
}

export default SalesDiscountAmountPresenter;
